// Passkeys/WebAuthn implementation for Supabase
import { supabase } from '../services/supabase';

export interface PasskeyCredential {
  id: string;
  credential_id: string;
  public_key: string;
  counter: number;
  device_type: string;
  name: string;
  created_at: string;
  last_used_at: string;
}

export class PasskeysService {
  private static readonly RP_NAME = 'Wishful';
  private static readonly RP_ID = window.location.hostname;

  /**
   * Register a new passkey for the current user
   */
  static async registerPasskey(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🚀 Starting passkey registration...');
      console.log('  User ID:', userId);
      
      // Generate challenge
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);

      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge,
          rp: {
            name: this.RP_NAME,
            id: this.RP_ID,
          },
              user: {
                id: new TextEncoder().encode(userId),
                name: userId,
                displayName: 'Wishful Passkey',
              },
          pubKeyCredParams: [
            { type: 'public-key', alg: -7 }, // ES256
            { type: 'public-key', alg: -257 }, // RS256
          ],
          authenticatorSelection: {
            authenticatorAttachment: 'platform', // Prefer built-in authenticators
            userVerification: 'preferred',
          },
          timeout: 60000,
          attestation: 'none',
        },
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('Failed to create passkey');
      }

      // Store credential in database
      console.log('💾 Storing passkey in database...');
      console.log('  User ID:', userId);
      console.log('  Credential ID:', credential.id.substring(0, 20) + '...');
      console.log('  Device Type:', this.getDeviceType());
      
      const insertData = {
        user_id: userId,
        credential_id: credential.id,
        public_key: this.arrayBufferToBase64((credential.response as AuthenticatorAttestationResponse).getPublicKey()!),
        counter: 0,
        device_type: this.getDeviceType(),
        name: 'Wishful Passkey',
      };
      
      console.log('📝 Insert data:', {
        user_id: insertData.user_id,
        credential_id: insertData.credential_id.substring(0, 20) + '...',
        device_type: insertData.device_type,
        name: insertData.name,
        public_key_length: insertData.public_key.length
      });

      const { data: insertResult, error } = await supabase
        .from('passkeys')
        .insert(insertData)
        .select();

      if (error) {
        console.error('❌ Failed to store passkey:', error);
        console.error('❌ Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      console.log('✅ Passkey insert result:', insertResult);
      
      if (!insertResult || insertResult.length === 0) {
        console.error('❌ Insert succeeded but no data returned!');
        throw new Error('Passkey insert returned no data');
      }

      // Verify the passkey was actually stored by trying to read it back
      console.log('🔍 Verifying passkey was stored...');
      
      // Add a small delay to ensure the insert has been committed
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const { data: verifyData, error: verifyError } = await supabase
        .from('passkeys')
        .select('id, name, credential_id, user_id')
        .eq('credential_id', credential.id)
        .single();
        
      if (verifyError || !verifyData) {
        console.error('❌ Passkey verification failed:', verifyError?.message);
        console.error('❌ This suggests RLS policies are blocking the insert despite success message');
        
        // Try to read all passkeys to see if any exist
        const { data: allPasskeys, error: allPasskeysError } = await supabase
          .from('passkeys')
          .select('*');
          
        if (!allPasskeysError && allPasskeys) {
          console.log('📋 Total passkeys in database:', allPasskeys.length);
          allPasskeys.forEach((pk, i) => {
            console.log(`  ${i + 1}. ${pk.name}: ${pk.credential_id}`);
          });
        } else {
          console.log('❌ Error reading all passkeys:', allPasskeysError?.message);
        }
        
        throw new Error('Passkey was not actually stored in database');
      }
      
      console.log('✅ Passkey verification successful:', verifyData);

      // Additional verification: Check if we can read all passkeys
      console.log('🔍 Final verification: Checking all passkeys in database...');
      const { data: finalCheck, error: finalError } = await supabase
        .from('passkeys')
        .select('*');
        
      if (!finalError && finalCheck) {
        console.log(`📋 Total passkeys in database: ${finalCheck.length}`);
        finalCheck.forEach((pk, i) => {
          console.log(`  ${i + 1}. ${pk.name}: ${pk.credential_id}`);
        });
      } else {
        console.log('❌ Error in final check:', finalError?.message);
      }

      console.log('✅ Passkey stored successfully!');
      
      // Test persistence after a delay
      console.log('🕐 Testing persistence after 1 second delay...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const { data: persistenceCheck, error: persistenceError } = await supabase
        .from('passkeys')
        .select('*')
        .eq('credential_id', credential.id);
        
      if (!persistenceError && persistenceCheck && persistenceCheck.length > 0) {
        console.log('✅ Passkey persistence confirmed after delay');
      } else {
        console.log('❌ Passkey disappeared after delay:', persistenceError?.message);
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Passkey registration failed:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to register passkey' 
      };
    }
  }

  /**
   * Authenticate using passkey
   */
  static async authenticateWithPasskey(): Promise<{ success: boolean; userId?: string; error?: string }> {
    try {
      // Check if passkeys are supported
      if (!this.isSupported()) {
        return { success: false, error: 'Passkeys are not supported on this device' };
      }

      // Generate challenge
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);

      // Try to authenticate without specifying allowCredentials first
      // This will use the browser's stored passkeys for this domain
      console.log('🔍 Attempting passkey authentication...');
      console.log('  Challenge generated:', challenge.length, 'bytes');
      
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge,
          timeout: 60000,
          userVerification: 'preferred',
        },
      }) as PublicKeyCredential;

      if (!assertion) {
        return { success: false, error: 'No passkey found on this device' };
      }

      // Get the credential ID from the assertion
      // Use assertion.id (base64url) to match how we store it during registration
      const credentialId = assertion.id;
      console.log('🔍 Authenticated credential ID:', credentialId.substring(0, 20) + '...');

      // Find this credential in our database
      console.log('🔍 Looking for credential ID:', credentialId);
      const { data: passkeyData, error: passkeyError } = await supabase
        .from('passkeys')
        .select('user_id, counter, credential_id, name')
        .eq('credential_id', credentialId)
        .maybeSingle();

      if (passkeyError || !passkeyData) {
        console.log('❌ Passkey not found in database. Error:', passkeyError?.message);
        
        // Let's also check what passkeys exist in the database
        const { data: allPasskeys, error: allPasskeysError } = await supabase
          .from('passkeys')
          .select('credential_id, name, user_id');
          
        if (!allPasskeysError && allPasskeys) {
          console.log('📋 Existing passkeys in database:', allPasskeys.length);
          allPasskeys.forEach((pk, i) => {
            console.log(`  ${i + 1}. ${pk.name}: ${pk.credential_id.substring(0, 20)}...`);
            console.log(`     Full credential ID: ${pk.credential_id}`);
            console.log(`     Looking for: ${credentialId}`);
            console.log(`     Exact match: ${pk.credential_id === credentialId}`);
            console.log(`     Length match: ${pk.credential_id.length === credentialId.length}`);
            console.log(`     User ID: ${pk.user_id}`);
          });
          
          // Show all users with passkeys
          const userIds = allPasskeys.map(pk => pk.user_id);
          const uniqueUsers = userIds.filter((userId, index) => userIds.indexOf(userId) === index);
          console.log(`👥 Users with passkeys: ${uniqueUsers.length}`);
          uniqueUsers.forEach((userId, i) => {
            const userPasskeys = allPasskeys.filter(pk => pk.user_id === userId);
            console.log(`  ${i + 1}. User ${userId}: ${userPasskeys.length} passkeys`);
          });
          
          // Try to find a partial match
          const partialMatch = allPasskeys.find(pk => pk.credential_id.substring(0, 20) === credentialId.substring(0, 20));
          if (partialMatch) {
            console.log('🔍 Found partial match:', partialMatch.credential_id);
          }
        } else {
          console.log('❌ Error fetching passkeys:', allPasskeysError?.message);
        }
        
        return { success: false, error: 'Passkey not found in our system. Please register a new passkey.' };
      }

      // Update counter
      await supabase
        .from('passkeys')
        .update({ 
          counter: passkeyData.counter + 1,
          last_used_at: new Date().toISOString()
        })
        .eq('credential_id', credentialId);

      return { 
        success: true, 
        userId: passkeyData.user_id 
      };
    } catch (error: any) {
      console.error('Passkey authentication failed:', error);
      
      // Handle specific WebAuthn errors
      if (error.name === 'NotAllowedError') {
        return { success: false, error: 'Authentication was cancelled or not allowed' };
      } else if (error.name === 'NotSupportedError') {
        return { success: false, error: 'Passkeys are not supported on this device' };
      } else if (error.name === 'SecurityError') {
        return { success: false, error: 'Security error during authentication' };
      }
      
      return { 
        success: false, 
        error: error.message || 'Authentication failed' 
      };
    }
  }

  /**
   * Get user's passkeys
   */
  static async getUserPasskeys(userId: string): Promise<PasskeyCredential[]> {
    try {
      const { data, error } = await supabase
        .from('passkeys')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to fetch passkeys:', error);
      return [];
    }
  }

  /**
   * Delete a passkey
   */
  static async deletePasskey(credentialId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('passkeys')
        .delete()
        .eq('credential_id', credentialId);

      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      console.error('Failed to delete passkey:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to delete passkey' 
      };
    }
  }

  /**
   * Check if passkeys are supported
   */
  static isSupported(): boolean {
    return !!(
      window.PublicKeyCredential &&
      navigator.credentials &&
      typeof navigator.credentials.create === 'function' &&
      typeof navigator.credentials.get === 'function'
    );
  }

  /**
   * Check if platform authenticator is available
   */
  static async isPlatformAuthenticatorAvailable(): Promise<boolean> {
    try {
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
      return false;
    }
  }

  // Utility methods
  private static arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private static base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private static getDeviceType(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('iphone') || userAgent.includes('ipad')) return 'iOS';
    if (userAgent.includes('android')) return 'Android';
    if (userAgent.includes('mac')) return 'macOS';
    if (userAgent.includes('windows')) return 'Windows';
    if (userAgent.includes('linux')) return 'Linux';
    return 'Unknown';
  }
}
